import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Keyboard, MousePointer, Monitor, Phone } from 'lucide-react';

interface AccessibilityFeature {
  icon: React.ElementType;
  title: string;
  description: string;
}

const features: AccessibilityFeature[] = [
  {
    icon: Keyboard,
    title: 'Keyboard Navigation',
    description: 'Full keyboard accessibility with visible focus indicators and logical tab order.'
  },
  {
    icon: Monitor,
    title: 'Screen Readers',
    description: 'ARIA labels, semantic HTML, and proper heading hierarchy for screen reader compatibility.'
  },
  {
    icon: MousePointer,
    title: 'Click Targets',
    description: 'Large, easily clickable buttons and links with adequate spacing.'
  },
  {
    icon: Phone,
    title: 'Responsive Design',
    description: 'Fully responsive layout that adapts to different screen sizes and orientations.'
  }
];

export default function AccessibilityPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-8">Accessibility Statement</h1>
          
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">Our Commitment</h2>
              <p className="text-gray-600">
                We are committed to ensuring digital accessibility for people with disabilities. We continually 
                improve the user experience for everyone and apply the relevant accessibility standards.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-6">Key Features</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="p-6 border border-gray-100 rounded-lg hover:border-brand-primary transition-colors">
                    <div className="flex items-center mb-4">
                      <feature.icon className="h-6 w-6 text-brand-primary mr-3" />
                      <h3 className="text-lg font-semibold">{feature.title}</h3>
                    </div>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Standards</h2>
              <p className="text-gray-600 mb-4">
                Our website follows WCAG 2.1 Level AA standards, including:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Sufficient color contrast ratios</li>
                <li>Alt text for all meaningful images</li>
                <li>Proper heading structure</li>
                <li>Descriptive link text</li>
                <li>Consistent navigation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Feedback</h2>
              <p className="text-gray-600">
                We welcome your feedback on the accessibility of our website. Please contact us at{' '}
                <a href="mailto:accessibility@pqstart.com" className="text-brand-primary hover:text-brand-dark">
                  accessibility@pqstart.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}